import React from 'react';
import { faCalendarPlus, faComment, faCommentAltLines, faExternalLink, faFileCertificate, faMailBulk, faMobileScreen, faUserTag } from '@fortawesome/pro-light-svg-icons';
import { SecureApiService } from '../../../api-service';
import {
  bookInterviewV2Path,
  citizenAreaPathName,
  citizenCvViewerPathName,
  cvJobsearchTabPathName,
  interviewTabPathName,
} from '../../../areas/citizen/authority/citizen-authority-area-pathnames';
import { ShowCitizensDialog } from '../modals/show-citizens-dialog/show-citizens-dialog';
import { IAction } from './interfaces';
import { standAlonePathName } from '../../../areas/area-pathnames';
import { joinURL } from '../../../utilities';
import { attendanceAreaPathName, attendanceBulkPath } from '../../../areas/attendance/attendance-area-pathnames';
import { bulkCourseEnrollmentCitizenIds } from '../../../areas/attendance/course-enrollment/bulk-course-enrollment/bulk-course-enrollment';
import { SELECTION_CAP } from '../../../areas/search/hooks';
import { forumBasePath, forumNudgePathName } from '../../../areas/forum/forum-area-pathnames';
import { forumNudgeCitizenIds } from '../../../areas/forum/caseworker/nudge/forum-nudge-tab';

export const nudgeInForumActionId = 'NudgeInForum';

export const actions: IAction[] = [
  {
    context: 'citizen',
    id: 'ShowCitizens',
    displayName: 'Vis borgere',
    action: async selectedEntries => <ShowCitizensDialog citizenIds={selectedEntries} />,
    icon: faCommentAltLines,
    isVisible: () => true,
    contextOnlyAction: true,
  },
  {
    context: 'citizen',
    id: 'InterviewNotice',
    displayName: 'Indkald til samtale',
    action: selectedEntries => {
      if (selectedEntries.length === 1) {
        const url = `${window.location.origin}/${citizenAreaPathName}/${selectedEntries[0]}/${interviewTabPathName}/${bookInterviewV2Path}`;
        window.open(url);
        return;
      }
      window.addEventListener('message', isReady, false);
      const url = '/citizens/interview/crowd-booking';
      const win = window.open(url) as any;
      const key = 'crowBookingCitizenIds';
      function isReady(event: MessageEvent) {
        if (event.data.topic === `${key}_Ready`) {
          win.postMessage({ data: selectedEntries, topic: `${key}_Topic` });
        }
      }
    },
    icon: faComment,
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: 'CreateCrowdLetter',
    displayName: 'Dan breve',
    action: selectedEntries => {
      const url = `${window.location.origin}/citizens/crowd-letter`;
      const popup = window.open(url);
      const timer = setInterval(() => {
        if (popup) {
          popup.postMessage({ type: 'crowdLetter', payload: selectedEntries }, window.location.origin);
          clearInterval(timer);
        }
      }, 7000);
    },
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    icon: faMailBulk,
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: 'PersonGroupMarking',
    displayName: 'Ny persongruppemarkering',
    action: selectedEntries => {
      const url = `${window.location.origin}/citizens/bulk-persongroupmarkings`;
      const popup = window.open(url);
      const timer = setInterval(() => {
        if (popup) {
          popup.postMessage({ type: 'bulkPersonGroupMarking', payload: selectedEntries }, window.location.origin);

          clearInterval(timer);
        }
      }, 2000);
    },
    icon: faUserTag,
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: 'CitizenCV',
    displayName: 'Vis CV',
    action: (selectedEntries, _selectedRootIds, _isFeatureEnabled, replaceUrl) => {
      selectedEntries.forEach(entry => {
        const url = joinURL(standAlonePathName, citizenAreaPathName, entry, cvJobsearchTabPathName, citizenCvViewerPathName);
        window.open(replaceUrl(url));
      });
    },
    icon: faFileCertificate,
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > 20 ? 'Antallet af valgte elementer må ikke overstige 20' : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: 'ShowLink',
    displayName: 'Vis link',
    action: selectedEntries => {
      let preText = '';
      let text = '';
      selectedEntries.forEach(entityId => {
        const link = `${window.location.origin}/citizen/${entityId.replace(/[{}]/g, '')}/details`;
        text += `${preText}${link}`;
        preText = '\r\n\r\n';
      });
      // eslint-disable-next-line no-alert
      window.prompt('Kopiér link til udklipsholder: Ctrl+C, Enter', text);
    },
    icon: faExternalLink,
    isVisible: selectedItemsCount => selectedItemsCount > 0,
    isDisabled: selectedItemsCount => (selectedItemsCount > 10 ? 'Antallet af valgte elementer må ikke overstige 10' : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: 'SchultzCaseLink',
    displayName: 'Send til Schultz Sag',
    action: async (selectedEntries, _selectedRootIds, _isFeatureEnabled, replaceUrl) => {
      const baseService: SecureApiService = new SecureApiService();
      const url = replaceUrl('https://{schultzSagHost}/api/ContextPush?muni={muni}');
      const result = await baseService.post<string[], string>(url, selectedEntries);
      window.open(result);
    },
    icon: faCommentAltLines,
    isVisible: (selectedItemsCount, isFeatureEnabled, isFeaturePurchased) =>
      selectedItemsCount > 0 && isFeaturePurchased(['enableSchultzCaseLink']) && !isFeatureEnabled('EnableSagWebApex'),
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: nudgeInForumActionId,
    displayName: 'Nudge i Forum',
    action: selectedEntries => {
      const popup = window.open(joinURL(forumBasePath, forumNudgePathName));
      const key = forumNudgeCitizenIds;
      function isReady(event: MessageEvent) {
        if (event.source === popup && event.data.topic === `${key}_Ready`) {
          popup?.postMessage({ data: selectedEntries, topic: `${key}_Topic` });
          window.removeEventListener('message', isReady);
        }
      }
      window.addEventListener('message', isReady, false);
    },
    icon: faMobileScreen,
    isVisible: (selectedItemsCount, isFeatureEnabled) => selectedItemsCount > 0 && isFeatureEnabled('EnableSagWebApex'),
    isDisabled: selectedItemsCount => (selectedItemsCount > SELECTION_CAP ? `Antallet af valgte elementer må ikke overstige ${SELECTION_CAP}` : ''),
    showOnUniverseAction: true,
  },
  {
    context: 'citizen',
    id: 'BulkCourseEnrollment',
    displayName: 'Opret fremmødeforløb',
    action: async (_selectedIds, _selectedRootIds, _isFeatureEnabled, _replaceUrl, selectedEntities) => {
      const popup = window.open(joinURL('/', attendanceAreaPathName, attendanceBulkPath));
      const key = bulkCourseEnrollmentCitizenIds;
      function isReady(event: MessageEvent) {
        if (event.data.topic === `${key}_Ready` && popup) {
          popup.postMessage({ data: selectedEntities, topic: `${key}_Topic` });
        }
      }
      window.addEventListener('message', isReady, false);
    },
    icon: faCalendarPlus,
    isVisible: (selectedItemsCount, isFeatureEnabled) => isFeatureEnabled('EnableAttendanceMassActionInFrontend') && selectedItemsCount > 0,
    isDisabled: (selectedItemsCount, _rowCount, _isLoading, selectedItems) => {
      if (selectedItemsCount > 50) {
        return 'Antallet af valgte elementer må ikke overstige 50';
      }
      const ids = selectedItems.map(item => item.rootEntityId);
      const hasCitizenDuplicates = new Set(ids).size !== ids.length;
      if (hasCitizenDuplicates) {
        return 'Den samme borger er valgt flere gange. Fjern dubletter for at oprette et fremmødeforløb.';
      }

      return '';
    },
    contextOnlyAction: true,
    showOnlyForContext: 'activities',
  },
];
